import React, { useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import {
  Zap, Search, MessageSquare, Users, LogOut, Menu, Loader2,
  LayoutDashboard, BarChart3, Workflow, DollarSign, Shield,
  ArrowRight, CheckCircle2, TrendingUp, ChevronRight, Share2, Copy, Check, Activity, Store,
} from 'lucide-react';
import { HSLogo } from '@/components/HSLogo';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const IncomeInsurance = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [salary, setSalary] = useState(55000);
  const [expenses, setExpenses] = useState('');
  const [riskComfort, setRiskComfort] = useState('moderate');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [sharing, setSharing] = useState(false);
  const [shareUrl, setShareUrl] = useState('');
  const [copied, setCopied] = useState(false);

  const handleCalculate = async () => {
    setLoading(true);
    setShareUrl('');
    setCopied(false);
    try {
      const payload = { current_salary: salary, risk_comfort: riskComfort };
      if (expenses) payload.monthly_expenses = parseFloat(expenses);
      const res = await axios.post(`${API}/income/insurance-calc`, payload);
      setResult(res.data);
    } catch (err) {
      toast.error('Calculation failed');
    } finally {
      setLoading(false);
    }
  };

  const handleShare = async () => {
    if (!result) return;
    setSharing(true);
    try {
      const res = await axios.post(`${API}/income/share`, {
        current_salary: salary,
        risk_comfort: riskComfort,
        milestones: result.milestones,
        dual_income_scenarios: result.dual_income_scenarios,
        key_insight: result.key_insight,
        monthly_per_client: result.monthly_per_client,
      });
      const url = `${window.location.origin}/shared/${res.data.share_id}`;
      setShareUrl(url);
      toast.success('Share link created!');
    } catch (err) {
      toast.error('Failed to create share link');
    } finally {
      setSharing(false);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    toast.success('Link copied!');
    setTimeout(() => setCopied(false), 3000);
  };

  const navItems = [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard' },
    { icon: Workflow, label: 'Workflow Builder', path: '/workflow' },
    { icon: BarChart3, label: 'Demand Intel', path: '/demand' },
    { icon: Search, label: 'Job Leads', path: '/jobs' },
    { icon: Users, label: 'Outbound Leads', path: '/crm' },
    { icon: MessageSquare, label: 'Pitch Generator', path: '/pitch' },
    { icon: Store, label: 'Marketplace', path: '/marketplace' },
    { icon: Activity, label: 'API Hub', path: '/hub' },
  ];

  const getMilestoneColor = (color) => {
    if (color === 'emerald') return { bg: 'bg-emerald-500/10', text: 'text-emerald-400', border: 'border-emerald-500/20', accent: 'bg-emerald-500' };
    if (color === 'violet') return { bg: 'bg-violet-500/10', text: 'text-violet-400', border: 'border-violet-500/20', accent: 'bg-violet-500' };
    return { bg: 'bg-amber-500/10', text: 'text-amber-400', border: 'border-amber-500/20', accent: 'bg-amber-500' };
  };

  return (
    <div className="min-h-screen bg-[#050505] flex">
      <div className="noise-overlay" />

      {/* Sidebar */}
      <aside className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-[#0a0a0a] border-r border-white/5 transform transition-transform lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="flex flex-col h-full">
          <div className="p-6 border-b border-white/5">
            <Link to="/" className="flex items-center gap-2">
              <HSLogo size={32} />
              <span className="font-black text-lg tracking-tight">HumanScaled</span>
            </Link>
          </div>
          <nav className="flex-1 p-4 space-y-1">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link key={item.path} to={item.path} className={`flex items-center gap-3 px-4 py-3 rounded-sm transition-colors ${isActive ? 'bg-emerald-500/10 text-emerald-400 border-l-2 border-emerald-500' : 'text-muted-foreground hover:text-white hover:bg-white/5'}`}>
                  <Icon className="w-5 h-5" />{item.label}
                </Link>
              );
            })}
          </nav>
          <div className="p-4 border-t border-white/5">
            <div className="flex items-center gap-3 px-4 py-3 bg-white/5 rounded-sm mb-3">
              <div className="w-8 h-8 bg-emerald-500/20 flex items-center justify-center text-emerald-400 font-bold text-sm">{user?.name?.charAt(0) || 'U'}</div>
              <div className="flex-1 min-w-0"><p className="text-sm font-medium truncate">{user?.name}</p><p className="text-xs text-muted-foreground truncate">{user?.email}</p></div>
            </div>
            <Button variant="ghost" onClick={() => { logout(); navigate('/'); }} className="w-full justify-start text-muted-foreground hover:text-white">
              <LogOut className="w-4 h-4 mr-2" /> Sign Out
            </Button>
          </div>
        </div>
      </aside>

      {sidebarOpen && <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />}

      <main className="flex-1 min-h-screen">
        <header className="sticky top-0 z-30 bg-[#050505]/80 backdrop-blur-md border-b border-white/5">
          <div className="flex items-center justify-between h-16 px-6">
            <div className="flex items-center gap-4">
              <button onClick={() => setSidebarOpen(true)} className="lg:hidden p-2 text-muted-foreground hover:text-white"><Menu className="w-6 h-6" /></button>
              <h1 className="text-lg font-bold" data-testid="calc-title">Income Insurance Calculator</h1>
            </div>
          </div>
        </header>

        <div className="p-6 lg:p-8 max-w-4xl mx-auto space-y-8">
          {/* Header */}
          <div className="text-center space-y-3" data-testid="calc-header">
            <div className="inline-flex items-center gap-2 px-3 py-1 border border-emerald-500/30 bg-emerald-500/10 text-emerald-400 text-sm font-mono">
              <Shield className="w-3.5 h-3.5" /> INCOME PROTECTION
            </div>
            <h2 className="text-2xl md:text-3xl font-bold">
              How many clients until your job is <span className="text-emerald-400">optional</span>?
            </h2>
            <p className="text-muted-foreground max-w-xl mx-auto">
              Not eliminated. Optional. See exactly what it takes to build a safety net that makes employment a choice, not a requirement.
            </p>
          </div>

          {/* Calculator Input */}
          <div className="bg-[#0a0a0a] border border-white/5 p-6 space-y-6" data-testid="calc-inputs">
            <div>
              <label className="text-xs text-muted-foreground uppercase tracking-wider mb-3 block">Your Current Annual Salary</label>
              <div className="flex items-center gap-4">
                <DollarSign className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                <Slider
                  value={[salary]}
                  onValueChange={([v]) => setSalary(v)}
                  min={20000}
                  max={200000}
                  step={5000}
                  className="flex-1"
                />
                <span className="font-mono text-lg font-bold w-28 text-right text-emerald-400" data-testid="salary-display">${salary.toLocaleString()}</span>
              </div>
              <div className="flex justify-between text-[10px] text-muted-foreground mt-1 px-6">
                <span>$20k</span><span>$100k</span><span>$200k</span>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-muted-foreground uppercase tracking-wider mb-2 block">Monthly Expenses (optional)</label>
                <Input
                  type="number"
                  placeholder="Auto-estimates at 75% of salary"
                  value={expenses}
                  onChange={e => setExpenses(e.target.value)}
                  className="bg-black/50 border-white/10 rounded-sm"
                  data-testid="expenses-input"
                />
              </div>
              <div>
                <label className="text-xs text-muted-foreground uppercase tracking-wider mb-2 block">Risk Comfort Level</label>
                <Select value={riskComfort} onValueChange={setRiskComfort}>
                  <SelectTrigger className="bg-black/50 border-white/10 rounded-sm" data-testid="risk-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0a0a0a] border-white/10">
                    <SelectItem value="conservative">Conservative (1.3x buffer)</SelectItem>
                    <SelectItem value="moderate">Moderate (1x match)</SelectItem>
                    <SelectItem value="aggressive">Aggressive (0.8x lean)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <Button
              onClick={handleCalculate}
              disabled={loading}
              className="w-full bg-emerald-500 text-black hover:bg-emerald-400 font-bold rounded-sm h-12"
              data-testid="calculate-btn"
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <>Calculate My Income Insurance <ArrowRight className="w-5 h-5 ml-2" /></>}
            </Button>
          </div>

          {/* Results */}
          {result && (
            <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500" data-testid="calc-results">
              {/* Key Insight */}
              <div className="bg-gradient-to-r from-emerald-500/10 to-violet-500/10 border border-emerald-500/20 p-6 text-center" data-testid="key-insight">
                <p className="text-2xl md:text-3xl font-bold mb-2">{result.key_insight}</p>
                <p className="text-muted-foreground text-sm">Based on ${result.monthly_per_client.toLocaleString()}/mo per client from your workflow</p>
                {!result.has_workflow && (
                  <div className="mt-3 flex items-center justify-center gap-2 text-xs text-amber-400">
                    <span>Using industry average. Build a workflow for personalized projections.</span>
                    <Button onClick={() => navigate('/workflow')} variant="outline" size="sm" className="border-amber-500/20 text-amber-400 text-xs h-7 rounded-sm">
                      Build Workflow
                    </Button>
                  </div>
                )}
              </div>

              {/* Milestones */}
              <div>
                <h3 className="text-sm font-bold uppercase tracking-wider text-muted-foreground mb-4 flex items-center gap-2">
                  <TrendingUp className="w-4 h-4" /> Your Path to Income Freedom
                </h3>
                <div className="grid md:grid-cols-3 gap-4">
                  {result.milestones.map((m, i) => {
                    const mc = getMilestoneColor(m.color);
                    return (
                      <div key={i} className={`bg-[#0a0a0a] border ${mc.border} p-5 relative overflow-hidden`} data-testid={`milestone-${i}`}>
                        <div className={`absolute top-0 left-0 h-1 ${mc.accent} transition-all`} style={{ width: `${Math.min(m.salary_percentage, 100)}%` }} />
                        <p className={`text-xs ${mc.text} uppercase tracking-wider mb-2`}>{m.name}</p>
                        <div className="flex items-baseline gap-1 mb-1">
                          <span className="font-mono text-3xl font-bold text-white">{m.clients_needed}</span>
                          <span className="text-sm text-muted-foreground">clients</span>
                        </div>
                        <p className="font-mono text-sm text-muted-foreground mb-3">
                          ${m.monthly_income.toLocaleString()}/mo
                          <span className={`ml-2 ${mc.text}`}>{m.salary_percentage}%</span>
                          <span className="text-xs"> of salary</span>
                        </p>
                        <p className="text-xs text-muted-foreground">{m.description}</p>
                        <p className={`text-xs ${mc.text} italic mt-3 pt-3 border-t border-white/5`}>"{m.message}"</p>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Dual Income Scenarios */}
              <div className="bg-[#0a0a0a] border border-white/5 p-6" data-testid="dual-income">
                <h3 className="text-sm font-bold uppercase tracking-wider text-muted-foreground mb-1">
                  Keep Your Job + Add Clients
                </h3>
                <p className="text-xs text-muted-foreground mb-4">You don't have to choose. See what adding clients does to your total income.</p>

                <div className="space-y-3">
                  {result.dual_income_scenarios.map((s, i) => {
                    const barWidth = Math.min(((s.total_annual - result.current_salary) / result.current_salary) * 100, 100);
                    return (
                      <div key={i} className="group" data-testid={`dual-scenario-${i}`}>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm">{s.label}</span>
                          <div className="flex items-center gap-3">
                            <span className="text-emerald-400 font-mono font-bold text-sm">{s.income_increase}</span>
                            <span className="font-mono text-sm text-white">${s.total_annual.toLocaleString()}<span className="text-muted-foreground text-xs">/yr</span></span>
                          </div>
                        </div>
                        <div className="w-full h-2 bg-white/5 flex">
                          <div className="h-full bg-white/15" style={{ width: '60%' }} />
                          <div className="h-full bg-emerald-500 transition-all" style={{ width: `${barWidth * 0.4}%` }} />
                        </div>
                        <div className="flex justify-between text-[10px] text-muted-foreground mt-0.5">
                          <span>Salary: ${result.monthly_salary.toLocaleString()}/mo</span>
                          <span className="text-emerald-400">+ ${s.side_income.toLocaleString()}/mo side income</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Share & Psychological Safety */}
              <div className="border border-white/5 p-6 text-center space-y-4" data-testid="safety-message">
                <p className="text-lg text-white/80 italic">
                  "You are not replacing your paycheck.<br />You are reducing dependence on a single source."
                </p>
                <p className="text-xs text-muted-foreground">
                  Employment is concentrated income. Fractionalization is diversified income.
                </p>

                {/* Share Section */}
                <div className="pt-4 border-t border-white/5 space-y-3">
                  {!shareUrl ? (
                    <Button
                      onClick={handleShare}
                      disabled={sharing}
                      variant="outline"
                      className="border-emerald-500/20 text-emerald-400 hover:bg-emerald-500/10 rounded-sm"
                      data-testid="share-btn"
                    >
                      {sharing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Share2 className="w-4 h-4 mr-2" />}
                      Share Your Results
                    </Button>
                  ) : (
                    <div className="space-y-2" data-testid="share-link-section">
                      <p className="text-xs text-emerald-400 font-medium">Share this link with others:</p>
                      <div className="flex items-center gap-2 max-w-lg mx-auto">
                        <div className="flex-1 bg-black/50 border border-white/10 px-3 py-2 text-sm font-mono text-muted-foreground truncate rounded-sm">
                          {shareUrl}
                        </div>
                        <Button
                          onClick={handleCopy}
                          variant="outline"
                          size="sm"
                          className={`border-white/10 rounded-sm px-3 ${copied ? 'text-emerald-400 border-emerald-500/20' : ''}`}
                          data-testid="copy-link-btn"
                        >
                          {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                        </Button>
                      </div>
                      <p className="text-[10px] text-muted-foreground">
                        "I only need {result.milestones?.[0]?.clients_needed} clients to make my job optional — see yours"
                      </p>
                    </div>
                  )}
                </div>

                <div className="flex justify-center gap-3 pt-3">
                  <Button onClick={() => navigate('/workflow')} className="bg-emerald-500 text-black hover:bg-emerald-400 font-bold rounded-sm" data-testid="start-building-btn">
                    Start Building <ArrowRight className="w-4 h-4 ml-1" />
                  </Button>
                  <Button onClick={() => navigate('/demand')} variant="outline" className="border-white/10 rounded-sm" data-testid="view-demand-btn">
                    View Demand
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Pre-calculation motivation */}
          {!result && (
            <div className="text-center space-y-4 py-8">
              <p className="text-muted-foreground italic">"Security does not come from one employer. Security comes from optionality."</p>
              <p className="text-xs text-muted-foreground">Enter your salary above and see your path to income diversification.</p>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default IncomeInsurance;
